import Loader from "components/DesignSystem/Loader/loader";
import classNames from "classnames";
import { ConditionalLink } from "components/conditionalLink";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Modal from "components/DesignSystem/Modal/Modal";
import Tab from "components/DesignSystem/Tab/Tab";
import HelpIcon from "components/icons/helpIcon";
import { Redirect } from "components/icons/Redirect";
import { PHONE } from "constants/help";
import { ONBOARDING_LINK } from "constants/meetingRequestUrls";
import {
  ENTITY_DETAILS_STEP,
  US_ENTITY_DETAILS_V2,
} from "constants/onBoarding";
import { CurrentGroupContext } from "contexts/CurrentGroupContext";
import { useContext, useEffect, useState } from "react";
import {
  useGetEntityOnboardingProductsQuery,
  useGetOnboardingProductsQuery,
  useUpdateProductStatusMutation,
} from "store/apis/productOnboarding";
import { Step } from "types/Models/onboarding";
import { openLink } from "utils/openLink";
import { OnboardingStep } from "./OnboardingStep";
import { OnboardingStepper } from "./OnboardingStepper/OnboardingStepper";
import * as PRODUCT_ONBOARDING from "constants/productOnboardings";
import Breadcrumb from "components/DesignSystem/Breadcrumb/Breadcrumb";
import { SUBSCRIPTION_COMPLETE } from "constants/fieldTypes";
import { Sparkle } from "components/icons/Sparkle";
import { BOOKS_PLUS_PLAN } from "constants/addons";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { useAppSelector } from "hooks/useAppSelector";
import { InkleBooks } from "components/icons/Logos/InkleBooks";
import { StepRow } from "pages/Books/BooksHome/GetStartedCard";

const BOOKS_STEP_DESCRIPTIONS: Record<string, string> = {
  "Bookkeeping Preferences":
    "Configure Inkle Books with your accounting details",
  "Choose bookkeeping plan": "Pick the plan that fits your business",
  "Connect  bank account":
    "Link your banks, revenue & payroll apps to start syncing",
  "Share Accountant access": "Give Inkle secure access to your bank data.",
};

type OnboardingWidgetProps = {
  open: boolean;
  onClose: any;
  currentTab?: string;
};

export const OnboardingWidget = ({
  open,
  onClose,
  currentTab = "Tax",
}: OnboardingWidgetProps) => {
  const [currentProductId, setCurrentProductId] = useState<string>();
  const [currentStep, setCurrentStep] = useState<Step>();
  const group = useContext(CurrentGroupContext);
  const usEntities = group?.entities?.filter((e) => e.country_code === "US");
  const defaultEntity =
    currentStep?.step_metadata?.current_entity_id || usEntities?.[0]?.uuid;
  const [selectedEntity, setSelectedEntity] = useState(defaultEntity);

  let { data: products, isLoading: isLoadingProducts } =
    useGetOnboardingProductsQuery(
      {
        groupId: group?.uuid!,
      },
      { skip: !group?.uuid || currentTab === PRODUCT_ONBOARDING.SALES_TAX }
    );

  const entityId = useAppSelector((state) => state.onboardingWidget.entityId);

  const { data: entityProducts, isLoading: isLoadingEntityProducts } =
    useGetEntityOnboardingProductsQuery(
      {
        groupId: group?.uuid!,
        entityId: entityId!,
      },
      {
        skip:
          !group?.uuid ||
          !entityId ||
          currentTab !== PRODUCT_ONBOARDING.SALES_TAX,
      }
    );

  products = entityProducts || products;

  const [updateProductStatus] = useUpdateProductStatusMutation();

  useEffect(() => {
    setCurrentProductId(
      products?.find((product) => product.name === currentTab)?.uuid
    );
  }, [currentTab, products?.length]);

  useEffect(() => {
    setSelectedEntity(defaultEntity);
  }, [defaultEntity]);

  const currentProduct = products?.find((p) => p.uuid === currentProductId);
  const isWidgetLoading =
    isLoadingProducts ||
    isLoadingEntityProducts ||
    (Boolean(products?.some((p) => p.name === currentTab)) && !currentProduct);
  const isFirstStepNotStarted =
    currentStep?.order === 0 && currentStep?.status === "NOT_STARTED";
  const isCurrentStepDisabled =
    currentStep?.status === "COMPLETED" &&
    !isFirstStepNotStarted &&
    currentStep.type !== SUBSCRIPTION_COMPLETE;

  const completeOnboarding = async (
    groupId: string,
    productId: string,
    closeModal: boolean
  ) => {
    await updateProductStatus({
      groupId,
      productId,
      payload: {
        product_status: "COMPLETED",
      },
    });

    if (closeModal) {
      onClose();
    }
  };

  const { data: booksPlans = [] } = useGetSubscriptionsQuery(
    {
      subscription_types: BOOKS_PLUS_PLAN,
      groupId: group?.uuid,
      entityId: selectedEntity,
    },
    { skip: !group?.uuid || !selectedEntity }
  );

  const isBooksPlusPlanActive = booksPlans?.find(
    ({ group_subscription = [] }) => group_subscription?.length > 0
  );

  let currentProductName = currentProduct?.name;

  if (currentProductName === PRODUCT_ONBOARDING.TAXV2) {
    currentProductName = "Tax";
  }

  const isSalesTaxOnboarding =
    currentProductName === PRODUCT_ONBOARDING.SALES_TAX;
  const isBooksOnboarding = currentProduct?.name === PRODUCT_ONBOARDING.BOOKS;
  const totalSteps = currentProduct?.steps?.length ?? 0;
  const currentStepNumber =
    (currentProduct?.steps?.findIndex(
      (step) => step.name === currentStep?.name
    ) ?? -1) + 1;

  return (
    <>
      <Modal.Root open={open} onOpenChange={onClose}>
        <Modal.Content
          useCustomOverlay
          size="fullscreen"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          {isWidgetLoading && (
            <div className="t-flex t-h-full t-w-full t-items-center t-justify-center">
              <Loader />
            </div>
          )}
          {!isWidgetLoading && (
            <Tab.Root
              onValueChange={(e) =>
                setCurrentProductId(
                  products?.find((product: any) => product.name === e)?.uuid
                )
              }
              value={currentProduct?.name}
              className="t-h-full t-flex t-flex-col"
            >
              {!isBooksOnboarding && (
                <Modal.Header className="t-pb-0">
                  <Modal.Title className="t-flex t-flex-col t-gap-2">
                    {!isSalesTaxOnboarding && (
                      <Breadcrumb.Root>
                        <Modal.RawClose asChild>
                          <span>
                            <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
                          </span>
                        </Modal.RawClose>
                        <Breadcrumb.Item>
                          {currentProductName} Onboarding
                        </Breadcrumb.Item>
                      </Breadcrumb.Root>
                    )}
                    {!isSalesTaxOnboarding && (
                      <div className="t-text-subtext t-text-text-100 t-mb-4">
                        Inkle {currentProductName} Onboarding
                      </div>
                    )}
                    {isSalesTaxOnboarding && (
                      <div className="t-text-subtext t-text-text-100 t-mb-4">
                        Add business details
                        <p className="t-text-text-60 t-text-body-sm t-mt-2">
                          Provide following details to get started with
                          registration{" "}
                        </p>
                      </div>
                    )}
                  </Modal.Title>
                  <div className="t-flex t-items-center t-gap-2 t-mb-4">
                    {!isSalesTaxOnboarding && (
                      <ConditionalLink to={ONBOARDING_LINK} target="_blank">
                        <Button size="small" type="button">
                          Schedule onboarding call
                        </Button>
                      </ConditionalLink>
                    )}

                    {!isSalesTaxOnboarding && (
                      <Dropdown.Root>
                        <Dropdown.Trigger asChild>
                          <div>
                            <Button customType="ghost_icon" size="xsmall">
                              <div className="t-text-neutral-80">
                                <HelpIcon color="currentColor" />
                              </div>
                            </Button>
                          </div>
                        </Dropdown.Trigger>
                        <Dropdown.Portal>
                          <Dropdown.Content sideOffset={5} align="end">
                            <Dropdown.Item
                              onClick={() => openLink("/chat")}
                              className="t-text-text-60 t-text-subtext-sm t-flex t-gap-0.5"
                            >
                              Inkle Chat <Redirect size={16} />
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => openLink(`https://wa.me/${PHONE}`)}
                              className="t-text-text-60 t-text-subtext-sm t-flex t-gap-0.5"
                            >
                              Whatsapp <Redirect size={16} />
                            </Dropdown.Item>
                          </Dropdown.Content>
                        </Dropdown.Portal>
                      </Dropdown.Root>
                    )}
                    <Modal.Close />
                  </div>
                </Modal.Header>
              )}
              <div className="t-grid t-grid-cols-[1fr_3fr] t-overflow-auto t-grow sm:t-grid-cols-1 sm:t-auto-rows-min">
                <div className="t-p-6 t-bg-surface-lighter-grey t-flex t-flex-col t-justify-between sm:t-contents">
                  <div className="sm:t-bg-surface-lighter-grey sm:t-p-4">
                    {isBooksOnboarding && (
                      <div className="t-flex t-items-center t-gap-2 t-mb-6">
                        <InkleBooks />
                      </div>
                    )}
                    {isBooksOnboarding && (
                      <div className="t-hidden sm:t-flex t-flex-col t-gap-2">
                        <span className="t-text-caption t-text-text-30">
                          Step {currentStepNumber} of {totalSteps}
                        </span>
                        <span className="t-text-subtext t-text-text-100">
                          {currentStep?.name}
                        </span>
                        <div className="t-flex t-gap-1">
                          {currentProduct?.steps?.map((step, i) => (
                            <span
                              key={step.uuid}
                              className={classNames(
                                "t-h-1 t-flex-1 t-rounded-full",
                                {
                                  "t-bg-purple": i < currentStepNumber,
                                  "t-bg-neutral-10": i >= currentStepNumber,
                                }
                              )}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {isBooksOnboarding ? (
                      <div className="t-flex t-flex-col sm:t-hidden">
                        {currentProduct?.steps?.map((step, i) => {
                          const { name, status, uuid } = step;
                          const isCompleted = status === "COMPLETED";
                          const isActive = currentStep?.name === name;
                          const isDisabled =
                            status === "NOT_STARTED" && !isActive;
                          const isLast = i === currentProduct.steps.length - 1;

                          return (
                            <div
                              key={uuid}
                              className={classNames({
                                "t-cursor-pointer": status !== "NOT_STARTED",
                              })}
                              onClick={() =>
                                status !== "NOT_STARTED" && setCurrentStep(step)
                              }
                            >
                              <StepRow
                                title={name}
                                description={BOOKS_STEP_DESCRIPTIONS[name]}
                                isActive={isActive}
                                isCompleted={isCompleted}
                                isDisabled={isDisabled}
                                isLast={isLast}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <OnboardingStepper size="small">
                        {currentProduct?.steps?.map((step, i) => {
                          const { name, status, uuid } = step;
                          const isStepClickable =
                            currentProduct.name === PRODUCT_ONBOARDING.TAX ||
                            currentProduct.name === PRODUCT_ONBOARDING.TAXV2;

                          const showEntities =
                            step?.type === ENTITY_DETAILS_STEP &&
                            currentStep?.type === ENTITY_DETAILS_STEP &&
                            currentStep?.step_key === US_ENTITY_DETAILS_V2;

                          return (
                            <OnboardingStepper.Step
                              key={uuid}
                              notFirstStep={i !== 0}
                              notLastStep={i < currentProduct.steps.length - 1}
                              isCompleted={status === "COMPLETED"}
                              isActive={currentStep?.name === name}
                              isPending={status === "NOT_STARTED"}
                              step={i + 1}
                              onClick={() => setCurrentStep(step)}
                              clickable={isStepClickable}
                              showEntities={showEntities}
                              selectedEntity={selectedEntity}
                              formStepId={currentStep?.uuid}
                              isCurrentStepDisabled={isCurrentStepDisabled}
                              setSelectedEntity={setSelectedEntity}
                            >
                              <div className="t-mt-1">{name}</div>
                            </OnboardingStepper.Step>
                          );
                        })}
                      </OnboardingStepper>
                    )}
                  </div>
                  {isBooksOnboarding && (
                    <div className="t-flex t-flex-col t-gap-4 t-p-6 sm:t-order-last sm:t-p-4 sm:t-bg-surface-lighter-grey">
                      <div>
                        <div className="t-text-subtitle t-text-text-100">
                          Need help?
                        </div>
                        <div className="t-text-body-sm t-text-text-30">
                          Contact us, and we'll help you through the process.
                        </div>
                      </div>
                      <div className="t-flex t-items-center t-gap-2">
                        <ConditionalLink to={ONBOARDING_LINK} target="_blank">
                          <Button size="small" type="button">
                            Schedule onboarding call
                          </Button>
                        </ConditionalLink>
                        <Dropdown.Root>
                          <Dropdown.Trigger asChild>
                            <div>
                              <Button customType="ghost_icon" size="xsmall">
                                <div className="t-text-neutral-80">
                                  <HelpIcon color="currentColor" width="20" />
                                </div>
                              </Button>
                            </div>
                          </Dropdown.Trigger>
                          <Dropdown.Portal>
                            <Dropdown.Content sideOffset={5} align="end">
                              <Dropdown.Item
                                onClick={() => openLink("/chat")}
                                className="t-text-text-60 t-text-subtext-sm t-flex t-gap-0.5"
                              >
                                Inkle Chat <Redirect size={16} />
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() =>
                                  openLink(`https://wa.me/${PHONE}`)
                                }
                                className="t-text-text-60 t-text-subtext-sm t-flex t-gap-0.5"
                              >
                                Whatsapp <Redirect size={16} />
                              </Dropdown.Item>
                            </Dropdown.Content>
                          </Dropdown.Portal>
                        </Dropdown.Root>
                      </div>
                    </div>
                  )}
                </div>
                <div className="t-flex t-flex-col t-overflow-y-auto t-relative sm:t-overflow-visible">
                  {isBooksOnboarding && (
                    <div className="t-absolute t-top-4 t-right-4 t-z-10">
                      <Modal.Close />
                    </div>
                  )}
                  {isBooksPlusPlanActive &&
                    !isBooksOnboarding &&
                    currentProduct?.name === PRODUCT_ONBOARDING.BOOKS && (
                      <div className="t-bg-purple-10 t-p-2 t-text-body-sm t-text-purple t-flex t-justify-center t-items-center t-gap-2">
                        <Sparkle />
                        Congratulations! You are now on Inkle Books Standard
                        Plan
                      </div>
                    )}
                  <Modal.Body className="t-pr-0 t-pl-0 t-pb-0 t-flex-grow !t-overflow-hidden">
                    <Tab.Content
                      className="t-h-full"
                      value={currentProduct?.name || ""}
                    >
                      {currentProduct && (
                        <OnboardingStep
                          groupId={group?.uuid || ""}
                          currentProduct={currentProduct}
                          completeOnboarding={completeOnboarding}
                          currentStep={currentStep}
                          setCurrentStep={setCurrentStep}
                          isCurrentStepDisabled={isCurrentStepDisabled}
                          onClose={onClose}
                          selectedEntity={selectedEntity}
                        />
                      )}
                    </Tab.Content>
                  </Modal.Body>
                </div>
              </div>
            </Tab.Root>
          )}
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
